<template>
    <div>
        <steps :currentStep="currentStep" @switchStep="switchStep" :class="{ 'notAllowed': $v.form.$invalid || !avatarRequirements.isSizeOk || !avatarRequirements.isFormatOk }"></steps>

        <div class="new-product">
            <div class="my-4">
                <div v-if="step === currentStep">
                    <form @submit.prevent.stop="switchStep" novalidate @input="productAcivateUpdateFlag('details')">
                        <div class="d-flex justify-content-end align-items-center my-4 px-4">
                            <button type="button" class="btn cancel wider mr-3" @click="cancel">Cancel</button>
                            <button type="button" class="btn green wider mr-3" :disabled="$v.form.$invalid" @click="readyToSave" v-if="getSelectedItem !== null">
                                Save
                            </button>
                            <button type="button" class="btn blue wider" :disabled="$v.form.$invalid || !avatarRequirements.isSizeOk || !avatarRequirements.isFormatOk" @click="nextStep">Next <i class="ico-arrow-right fs-normal ml-2"></i></button>
                        </div>

                        <div class="px-4">
                            <div class="row">
                                <div class="col-sm-7">
                                    <avatar-uploader
                                        :value="currentImage"
                                        :originalImage="originalForm && (originalForm.imageLogoBase64 || originalForm.imageLogo)"
                                        :showPreview="false"
                                        :showButton="true"
                                        :showRequirements="true"
                                        :requirementsHorizontal="true"
                                        :backgroundCard="true"
                                        :buttonType="'picture'"
                                        :isSizeOk.sync="avatarRequirements.isSizeOk"
                                        :isFormatOk.sync="avatarRequirements.isFormatOk"
                                        :image.sync="form.imageLogo"
                                        :imageBase64.sync="form.imageLogoBase64"
                                        :imageHash.sync="form.imageLogoHash"
                                        :originalImageSync.sync="originalForm.imageLogo"
                                        :originalImageBase64Sync.sync="originalForm.imageLogoBase64"
                                        :originalHashSync.sync="originalForm.imageLogoHash"
                                    />

                                    <div class="panel mt-0 shaded radius-top-left-none radius-top-right-none">
                                        <div class="form-group row">
                                            <div class="col-sm-12" :class="{ error: $v.form.name.$error }">
                                                <label class="full">
                                                    Product name
                                                    <span class="brand">
                                                        *
                                                    </span>
                                                    <input type="text" v-model="form.name" @input="$v.form.name.$touch">
                                                </label>

                                                <div v-if="$v.form.name.$error" class="messages">
                                                    <span v-if="!$v.form.name.required">Name is required.</span>
                                                    <span v-if="!$v.form.name.minLength">Name must have at least 3 characters.</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <div class="col-sm-7">
                                                <div v-if="getCategories">
                                                    <label class="full">Product categories<span class="brand">*</span></label>

                                                    <multiselect
                                                        v-model="form.categoryList"
                                                        :options="categories"
                                                        :multiple="true"
                                                        :taggable="false"
                                                        @input="categoriesChanged"
                                                        track-by="id"
                                                        label="name"
                                                        selectLabel=""
                                                        deselectLabel=""
                                                    >
                                                    </multiselect>
                                                </div>
                                            </div>

                                            <div v-if="getEventColors" class="col-sm-5">
                                                <label class="full">
                                                    Color
                                                </label>

                                                <color-picker :selectedId="form.color" @colorChanged="updateColor" :colors="getEventColors"></color-picker>
                                            </div>
                                        </div>

                                        <div class="form-group row my-4">
                                            <div class="col-sm-12">
                                                <checkbox v-model="form.isPrivate" @input="changePrivateStatus">
                                                    Private tour / Charter.
                                                    <span class="normal">
                                                        Only one booking per tour is allowed.
                                                    </span>
                                                </checkbox>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="panel shaded">
                                        <h5>Description</h5>

                                        <div class="form-group row">
                                            <div class="col-sm-12">
                                                <label class="full d-flex align-items-center mb-1">
                                                    <span>Brief description</span>
                                                    <i
                                                        class="fa fa-info-circle fs-normal dark has-tooltip ml-2"
                                                        v-tooltip="{
                                                            content: 'Add a short summary to highlight the key selling points of the experience.<br>It supports agents during search and is visible to customers after booking confirmation.',
                                                            html: true
                                                        }"
                                                    ></i>
                                                </label>
                                                <textarea v-model="form.descriptionBrief" rows="1"></textarea>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <div class="col-sm-12">
                                                <label class="full d-flex align-items-center">
                                                    <span>Product description</span> 
                                                    <i class="fa fa-info-circle fs-normal dark has-tooltip ml-2"
                                                        v-tooltip="'Provide a detailed description to help agents understand and confidently promote your experience'"></i>
                                                </label>
                                                <textarea rows="8" v-model="form.description"></textarea>
                                            </div>
                                        </div>

                                        <div class="form-group row">
                                            <div class="col-sm-12">
                                                <label class="full d-flex align-items-center">
                                                    <span>Ticket information</span> 
                                                    <i class="fa fa-info-circle fs-normal dark has-tooltip ml-2"
                                                        v-tooltip="{ 
                                                            content: 'Include important practical details such as terms & conditions, check-in instructions, or other notes.<br>This information will be shown to customers in the T&Cs section of the widget, in the booking confirmation email, and on the ticket.', 
                                                            html: true 
                                                            }">
                                                    </i>
                                                </label>
                                                <textarea v-model="form.descriptionMore" rows="30" placeholder="e.g. Cancellations and changes accepted up to 48 hours before the event"></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-sm-5">
                                    <div class="panel shaded mt-0" :class="{ error: $v.form.location.$error }">
                                        <h5>Location <span class="brand">*</span></h5>


                                        <label class="full">
                                            <input type="text" v-model="form.location" @input="$v.form.location.$touch" placeholder="e.g. Albufeira, Marina de Albufeira">
                                            <div class="fs-smaller normal mt-2">Insert a location or starting point for this product.</div>
                                        </label>

                                        <div v-if="$v.form.location.$error" class="messages">
                                            <span v-if="!$v.form.location.required">Location is required.</span>
                                        </div>
                                    </div>

                                    <div class="panel shaded">
                                        <h5>Notes</h5>

                                        <div class="fs-small dark mt-2 mb-3">
                                            This information will be displayed on the calendar and on new reservations page.
                                        </div>

                                        <textarea v-model="form.internalNote"></textarea>
                                    </div>

                                    <div class="panel shaded">
                                        <h5>Booking question</h5>

                                        <label class="full">
                                            <input type="text" v-model="form.bookingQuestion" placeholder="e.g. Do you have the required fishing license?">
                                            <div class="fs-smaller normal mt-2">Use this if you want to add a question to the booking process.</div>
                                        </label>

                                        <checkbox v-model="form.bookingQuestionIsActive" @input="questionActiveChanged">
                                            Is question active.
                                        </checkbox>

                                        <checkbox class="ml-2" :disabled="!form.bookingQuestionIsActive" v-model="form.bookingQuestionIsMandatory">
                                            Is question mandatory.
                                        </checkbox>
                                    </div>


                                    <div class="panel shaded">
                                        <h5>Visibility</h5>

                                        <checkbox v-model="form.hideOnCalendar" @input="visibilityChanged">
                                            Plan Product <i class="fas fa-info-circle ml-1" v-tooltip="'Plan Product means that will not show on the calendar'"></i>
                                        </checkbox>

                                        <checkbox class="ml-2" :disabled="form.hideOnCalendar == false" v-model="form.showOnCalendarWhenHadBookings">
                                            Only show on calendar if event has bookings
                                        </checkbox>
                                    </div>

                                    <div class="panel shaded">
                                        <h5>Sellable status (based on the event start and end time)</h5>

										<p class="fs-small dark mb-2">
											Here you can choose if this product can be sellable after the event start time or until the end
										</p>
                                        <div class="d-flex mb-1 align-items-center w-100">
											<radio name="isSellableTillEnd" :value="true" v-model="form.isSellableTillEnd">
												It will be for sale until the end of the events
											</radio>
										</div>
                                        <div class="d-flex mb-3 align-items-center w-100">
											<radio name="isSellableTillEnd" :value="false" v-model="form.isSellableTillEnd">
												Sales are closed at event start time
											</radio>
										</div>
                                    </div>

                                    <div class="panel shaded">
                                        <h5>Product status</h5>

										<p v-if="hasActiveSchedules" class="fs-small dark mb-2">
											<i class="fas fa-info-circle"></i>
											You are not allowed to deactivate this product because it has active schedules
										</p>
                                        <div class="d-flex mb-3 align-items-center w-100">
											<span class="fs-small bold mr-2">Product is active:</span>
											<toggle-button
												v-model="form.active"
												:labels="{checked: 'Yes', unchecked: 'No'}"
												:disabled="hasActiveSchedules"
											/>
										</div>
                                    </div>

                                    <div class="panel shaded" v-if="getShowInvoiceOption">
                                        <h5>Primavera Settings</h5>

										<label class="full">
                                            Cost Center
                                            <input type="text" v-model="form.code">
                                        </label>
                                    </div>

                                    <ticket-extra-info v-if="false" :form.sync="form" @change="ticketExtraInfoChanged()" />
                                </div>
                            </div>
                        </div>

                        <div class="d-flex justify-content-end align-items-center my-4 px-4">
                            <button type="button" class="btn cancel wider mr-3" @click="cancel">Cancel</button>
                            <button
                                type="button"
                                class="btn green wider mr-3"
                                :disabled="$v.form.$invalid"
                                @click="readyToSave"
                                v-if="getSelectedItem !== null"
                            >
                                Save
                            </button>
                            <button type="button" class="btn blue wider" :disabled="$v.form.$invalid || !avatarRequirements.isSizeOk || !avatarRequirements.isFormatOk" @click="nextStep">Next <i class="ico-arrow-right fs-normal ml-2"></i></button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import { required, minLength } from 'vuelidate/lib/validators'
    import { eventHub } from '../../../main'
    import { VueEditor } from 'vue2-editor'

    import extendedVSelect from '@/extends/vue-select'
    import Steps from './ProductSteps'
    import AvatarUploader from '@/components/common/AvatarUploader.vue'
    import ColorPicker from '@/tools/ColorPicker'
    import Multiselect from 'vue-multiselect'
    import TicketExtraInfo from './components/TicketExtraInfo'

    export default {
        mixins: [],
        props: {
            currentStep: Number
        },
        data() {
            return {
                step: 1,
                form: {
                    version: 1,
                    resetItemsWhenEditing: false,
                    name: null,
                    code: null,
                    bookingQuestion: null,
                    bookingQuestionIsActive: false,
                    bookingQuestionIsMandatory: false,
                    capacity: 0,
                    category: {},
                    categoryList: [],
                    color: null,
                    isPrivate: false,
                    tax: true,
                    productCommercialAvailability: 1,
                    imageLogo: null,
                    imageLogoBase64: null,
                    hasManualConfirmation: false,
                    salesPermissionsAgents: true,
                    salesPermissionsAgentsCommissionValue: null,
                    salesPermissionsAgentsCustom: false,
                    agentsInFutureAreAllowed: true,
                    salesPermissionsTeam: true,
                    salesPermissionsWebsite: true,
                    routes:[],
                    groups: [],
                    hideOnCalendar: false,
                    showOnCalendarWhenHadBookings: false,
                    active: false,
                    isSellableTillEnd: false,
                    location: null
                },
                originalForm: null,
                avatarRequirements: {
                    isSizeOk: true,
                    isFormatOk: true
                },
                selectedCategory: null,
                selectedColor: null,
                updateFlagsEnabled: false,
            }
        },
        validations: {
            form: {
                name: {
                    required,
                    minLength: minLength(3),
                },
                categoryList: {
                    required,
                    minLength: minLength(1)
                },
                location: {
                    required
                },
            }
        },
        created() {
            if (this.getSelectedItem && !this.getClonedItem) {
                Object.assign(this.form, JSON.parse(JSON.stringify(this.getSelectedItem)));
            } else if (this.getClonedItem) {
                // EDITING PRODUCT
                Object.assign(this.form, this.getClonedItem);
            } else {
                // CREATE PRODUCT
                this.form.active = true;
                this.updateType({id:1});

                // add sales channel info
                if (this.getSalesChannels.length == 0) {
                    this.downloadSalesChannels().then(r => {
                        this.$set(this.form, 'channelCommissions', JSON.parse(JSON.stringify(this.getSalesChannelsForNewProduct)));
                    })
                } else {
                    this.$set(this.form, 'channelCommissions', JSON.parse(JSON.stringify(this.getSalesChannelsForNewProduct)));
                }
            }

            // helepr bus to force update, prevents pricing not being updated after change on split price modal
            eventHub.$on('forceUpdate',() => {
                this.$forceUpdate();
            });

            // if flag salesPermissionsAgentsCustom is not set, we set as false
            if (this.form.salesPermissionsAgentsCustom == undefined ) {
                this.$set(this.form, 'salesPermissionsAgentsCustom', false);
            }

            // if there is no agentsInFutureAreAllowed (yes = true | no = false) we set it to true
            if (this.form.agentsInFutureAreAllowed == undefined) {
                this.$set(this.form, 'agentsInFutureAreAllowed', true);
            }

            // sets Name, from, to, duration
            // without this, if we edit an existing product and jump to step "pickups" and press save, the groups will not have any dates and times (api will return error)
            // this ternary operator allows to return to step 1 from step 2 when creating and still having the dateFrom dateTo etc
            if (this.form.groups) {
                this.form.groups.map(group => {
                    if(group.schedules[0]){
                        group.dateFrom = group.schedules[0].dateFrom? group.schedules[0].dateFrom : group.dateFrom;
                        group.dateTo = group.schedules[0].dateTo? group.schedules[0].dateTo : group.dateTo;
                        group.duration = group.schedules[0].duration? group.schedules[0].duration : group.duration;
                    }
                })
            }

            // setting VAT Type
            if (this.form.productGenderPrices) {
                for (let pricingRow of this.form.productGenderPrices) {
                    pricingRow.vatTypeHelper = this.taxes.filter((audienceType) => {
                        return audienceType.id == pricingRow.vatType;
                    })[0]
                }
            }

            // enable updateFlags
            this.updateFlagsEnabled = true;
            this.setStartingColor();
            this.setStartingCategory();

            this.originalForm = JSON.parse(JSON.stringify(this.form));

        },
        computed: {
            ...mapGetters([
                'getSelectedItem',
                'getClonedItem',
                'account',
                'getSalesChannels',
                'getSalesChannelsForNewProduct',
                'getVatTypes',
                'getAudienceTypeList',
                'getEventColors',
                'getCategories',
                'getShowInvoiceOption'
            ]),
			hasActiveSchedules() {
				if (this.form.groups) {
					for (let group of this.form.groups) {
						if (group.schedules && group.schedules.length > 0) {
							return true;
						}
					}
				}
			},
            categories() {
                return this.getCategories;
            },
            taxes() {
                return this.getVatTypes;
            },
            audienceTypes() {
                return this.getAudienceTypeList;
            },
            currentImage() {
                return this.form.imageLogoBase64 || this.form.imageLogo || null;
            },
        },
        methods: {
            ...mapActions([
                'initCategories',
                'openModal',
                'updateroductEditPriceData',
                'downloadSalesChannels'
            ]),
            cancel() {
                this.$emit('cancelling');
            },
            switchStep(step) {
                if (this.$v.form.$invalid || !this.avatarRequirements.isSizeOk || !this.avatarRequirements.isFormatOk) return;
                this.$emit('goToStep', { invalid: this.$v.form.$invalid, form: this.form, step: (step !== undefined && !isNaN(step)) ? step : (this.currentStep + 1) });
            },
            nextStep(step) {
                this.$emit('goToStep', { invalid: false, form: this.form, form: this.form, step: (step !== undefined && !isNaN(step)) ? step : (this.currentStep + 1) });
            },
            readyToSave() {
                // Avatar validation
                const avatarInvalid = !this.avatarRequirements.isSizeOk || !this.avatarRequirements.isFormatOk;
                if (avatarInvalid) {
                    this.$dialog
                        .alert(
                            '\
                        <div class="fs-larg font-weight-600 mb-4">Invalid configuration</div>\
                        <div>Please check the fields marked red, and try again.</div>',
                            { okText: "Ok" }
                        )
                        .then(function () {});
                    return;
                }

                this.$emit('readyToSave', { invalid: false, form: this.form });
                this.originalForm = JSON.parse(JSON.stringify(this.form));
            },
            categoriesChanged(e) {
                this.productAcivateUpdateFlag('details')
            },
            setStartingColor() {
                if (this.getEventColors.length == 0) {
                    return false
                }

                if (this.form.color !== undefined && this.form.color !== null) {
                    this.selectedColor = this.getEventColors.find(item => item.id === this.form.color);
                } else {
                    this.selectedColor = this.getEventColors[12];
                    this.form.color = this.getEventColors[12].id;
                }
            },
            updateColor(color) {
                this.productAcivateUpdateFlag('details');
                this.form.color = color.id || null;
                this.$forceUpdate();
            },
            setStartingCategory() {
                if (this.form.category !== undefined && Object.keys(this.form.category).length !== 0) {
                    this.selectedCategory = this.getCategories.find(item => item.id === this.form.category.id);
                } else {
                    this.selectedCategory = this.getCategories[0];
                    this.form.category = this.getCategories[0];
                }
            },
            updateType(selectedType){
                if (selectedType.id == 1) {
                    if (this.form.productGenderPrices === undefined || this.form.productGenderPrices[0].genderPriceType != 1) {
                        this.$set(this.form, 'productGenderPrices', [
                            {
                                genderPriceType: 1,
                                vatType: 0,
                                value: 0,
                                currency: "€"
                            }
                        ])
                    }
                } else {
                    try {
                            this.$set(this.form, 'productGenderPrices', [
                                {
                                    genderPriceType: 2,
                                    audienceType: this.audienceTypes[0].id,
                                    audienceTypeDescription: this.audienceTypes[0].description,
                                    audienceTypeHelper: this.audienceTypes[0],
                                    value: 0,
                                    currency: '€',
                                    vatType: this.taxes[0].id,
                                    vatTypeHelper: this.taxes[0],
                                    productGenderPriceComponents: []
                                }
                            ])
                    } catch (err) {}
                }
            },
            changePrivateStatus: function(e) {
                let vm = this;

                if (e) {
                    // private activates
                    if (this.form.groups && this.form.groups.length > 0) {
                        //check if the product has any shared resource group
                        let hasSharedResource = false;
                        this.form.groups.map(group => {
                            if (group.shareType == 1) {
                                hasSharedResource = true;
                            }
                        })

                        if (hasSharedResource) {
                            this.$dialog.confirm('\
                            <div class="fs-larg font-weight-600 mb-4">Set product as private?</div>\
                            <div>This product has shared resources, changing it to private will set those resources as "Not Shared", continue?</span></div>')
                            .then(function () {
                                vm.form.groups.map(group => {
                                    group.shareType = 0;
                                })
                            })
                            .catch(function () {
                                // pressed No
                                vm.form.isPrivate = false;
                            });
                        }
                    }
                }
            },
            questionActiveChanged(e) {
                if (e == false) {
                    this.form.bookingQuestionIsMandatory = false;
                }
            },
            visibilityChanged(e) {
                if (e == false) {
                    this.form.showOnCalendarWhenHadBookings = false;
                }
            },
            ticketExtraInfoChanged() {
                this.productAcivateUpdateFlag('details');
            },
        },
        components: {
            vSelect: extendedVSelect,
            Steps,
            ColorPicker,
            VueEditor,
            Multiselect,
            TicketExtraInfo,
            AvatarUploader,
        },
    }
</script>

<style lang="scss" scoped>
    @import '../../../assets/scss/_variables.scss';
    .pRelative {
        // fixes the bug that makes the page scroll to top when pressing a checkbox (the page must be scrolled down for this to happen)
        position: relative;
    }

    .colorCircle {
        width: 1.875rem;
        height: 1.875rem;

        &.selected {
            border-color: $dark;
            border-width: 0.1875rem; 
            border-style: solid;
            text-align: center;
            padding-top: 1px;
        }

        &.selected::before {
            font-family: 'PluraloIconSet' !important;
            content: "\E911";
            color: $lightest;
            font-size: 0.7rem;
        }
    }
</style>



// WEBPACK FOOTER //
// ProductEdit.vue?f5d1e9b4